/**
 * AlterMe Choice-Based Conjoint Study Data
 * ──────────────────────────────────────────
 * Source: CBC Study, n=1,001, 15 tasks/respondent, 4 concepts/task
 * Date: February 2026
 *
 * THIS FILE IS THE SINGLE SOURCE OF TRUTH.
 * Every number displayed in the app must trace back here or to calculations.ts.
 * Never hardcode study data anywhere else.
 */

import type {
  PricePoint,
  Feature,
  Segment,
  PaymentTerm,
  ConditionalShare,
  NoneAnalysis,
  StudyDesign,
  Assumptions,
  AssumptionGap,
} from '../types';

// ─── PRICE-LEVEL CHOICE RATES ───
// Concept-level: probability of choosing any given concept at this price.
// Includes none-choosers in the denominator.
export const PRICE_POINTS: PricePoint[] = [
  { price: 499,  conceptsShown: 9151, conceptsChosen: 3315, choiceRate: 0.3623, elasticity: null },
  { price: 999,  conceptsShown: 9100, conceptsChosen: 2080, choiceRate: 0.2286, elasticity: undefined },
  { price: 1499, conceptsShown: 8723, conceptsChosen: 1500, choiceRate: 0.1720, elasticity: undefined },
  { price: 1699, conceptsShown: 8778, conceptsChosen: 1224, choiceRate: 0.1394, elasticity: undefined },
  { price: 1899, conceptsShown: 9248, conceptsChosen: 1096, choiceRate: 0.1185, elasticity: undefined },
];

// Compute revenue/showing and elasticity
PRICE_POINTS.forEach((pt, i) => {
  pt.revenuePerShowing = pt.price * pt.choiceRate;
  if (i > 0) {
    const prev = PRICE_POINTS[i - 1];
    const pctRateChange = (pt.choiceRate - prev.choiceRate) / prev.choiceRate;
    const pctPriceChange = (pt.price - prev.price) / prev.price;
    pt.elasticity = pctRateChange / pctPriceChange;
  }
});

// Valid price options for dropdowns
export const VALID_PRICES = PRICE_POINTS.map(p => p.price);

// Quick lookup: price → choice rate
export const PRICE_TO_RATE: Record<number, number> = Object.fromEntries(
  PRICE_POINTS.map(p => [p.price, p.choiceRate])
);


// ─── FEATURE CONDITIONAL SHARE LIFT ───
// Lift = share when included - share when excluded (among product choosers).
// Ranked by magnitude of lift.
export const FEATURES: Feature[] = [
  { name: 'Smart Biometrics Ring', description: 'Tracks sleep, stress, heart rate, movement', whenIncluded: 0.5661, whenExcluded: 0.4339, lift: 0.1322, rank: 1, selections: 5217 },
  { name: 'Workout Sessions', description: 'On-demand cardio & strength workouts', whenIncluded: 0.5591, whenExcluded: 0.4409, lift: 0.1182, rank: 2, selections: 5152 },
  { name: 'Body Composition Analysis', description: 'DEXA scan tracks fat & muscle mass (2x/month)', whenIncluded: 0.5482, whenExcluded: 0.4518, lift: 0.0964, rank: 3, selections: 5052 },
  { name: 'Recovery Sessions', description: 'Recovery session programming', whenIncluded: 0.5435, whenExcluded: 0.4565, lift: 0.0870, rank: 4, selections: 5008 },
  { name: 'DNA Health Report', description: 'Fitness & nutrition insights from your DNA', whenIncluded: 0.5428, whenExcluded: 0.4572, lift: 0.0856, rank: 5, selections: 5002 },
  { name: 'Personalized Wellness Plan', description: 'Daily plans that adapt to your body & goals', whenIncluded: 0.5419, whenExcluded: 0.4581, lift: 0.0838, rank: 6, selections: 4994 },
  { name: 'Nutrition Tracking', description: 'Calorie counting, macro and meal guidance', whenIncluded: 0.5375, whenExcluded: 0.4625, lift: 0.0750, rank: 7, selections: 4953 },
];

// Feature names for convenience
export const FEATURE_NAMES = FEATURES.map(f => f.name);

// Quick lookup: name → lift
export const FEATURE_LIFT: Record<string, number> = Object.fromEntries(
  FEATURES.map(f => [f.name, f.lift])
);


// ─── SEGMENT DATA ───
// Three k-means clusters on part-worth utilities.
export const SEGMENTS: Segment[] = [
  {
    name: 'Price-Sensitive Buyers',
    shortName: 'Price-Sensitive',
    size: 527,
    percentage: 0.5265,
    avgNoneUtility: 0.9694,
    weight: 0.5265,
    characterization: 'Mainstream; moderate price sensitivity. Primary revenue opportunity.',
    color: '#14B8A6', // teal
    priceCurve: {
      499:  0.4567,
      999:  0.3764,
      1499: 0.3115,
      1699: 0.2556,
      1899: 0.2186,
    },
  },
  {
    name: 'Low-Interest Prospects',
    shortName: 'Low-Interest',
    size: 284,
    percentage: 0.2837,
    avgNoneUtility: 0.0345,
    weight: 0.2837,
    characterization: 'Near-zero demand at any price. Essentially unreachable.',
    color: '#EF4444', // red
    priceCurve: {
      499:  0.0457,
      999:  0.0049,
      1499: 0.0033,
      1699: 0.0012,
      1899: 0.0015,
    },
  },
  {
    name: 'Budget Seekers',
    shortName: 'Budget Seekers',
    size: 190,
    percentage: 0.1898,
    avgNoneUtility: 0.4930,
    weight: 0.1898,
    characterization: 'High demand at $499; cliff above $500. Would need a low-tier product.',
    color: '#F59E0B', // amber
    priceCurve: {
      499:  0.5757,
      999:  0.1533,
      1499: 0.0389,
      1699: 0.0241,
      1899: 0.0193,
    },
  },
];

// Overall choice rates by price (weighted by segment sizes — should match PRICE_TO_RATE)
export const SEGMENT_OVERALL_RATES: Record<number, number> = {
  499:  0.3623,
  999:  0.2286,
  1499: 0.1720,
  1699: 0.1394,
  1899: 0.1185,
};


// ─── PAYMENT TERMS ───
export const PAYMENT_TERMS: PaymentTerm[] = [
  { label: 'Pay in Full',             key: 'payInFull',             selections: 2975, shareRate: 0.3228 },
  { label: 'Monthly (0% APR)',        key: 'monthlyZeroApr',        selections: 3110, shareRate: 0.3375 },
  { label: 'Monthly (with interest)', key: 'monthlyWithInterest',   selections: 3130, shareRate: 0.3397 },
];

// Quick lookup
export const PAYMENT_TERM_SHARE: Record<string, number> = Object.fromEntries(
  PAYMENT_TERMS.map(p => [p.key, p.shareRate])
);


// ─── CONDITIONAL SHARE RATES ───
// Share among respondents who chose a product (excludes none-choosers).
// Higher than concept-level rates.
export const CONDITIONAL_SHARES: ConditionalShare[] = [
  { price: 499,  productSelections: 4815, shareRate: 0.5225 },
  { price: 999,  productSelections: 2080, shareRate: 0.2257 },
  { price: 1499, productSelections: 1500, shareRate: 0.1628 },
  { price: 1699, productSelections: 1224, shareRate: 0.1328 },
  { price: 1899, productSelections: 1096, shareRate: 0.1189 },
];


// ─── NONE ANALYSIS ───
export const NONE_ANALYSIS: NoneAnalysis = {
  overallNoneRate: 0.3853,
  alwaysNoneCount: 217,
  alwaysNonePercent: 0.217,
  neverNoneCount: 427,
  neverNonePercent: 0.427,
  addressableMarketCount: 784,
  addressableMarketPercent: 0.783,
};


// ─── STUDY DESIGN ───
export const STUDY_DESIGN: StudyDesign = {
  totalRespondents: 1001,
  tasksPerRespondent: 15,
  conceptsPerTask: 4,
  totalTaskResponses: 15015,
  productChoices: 9215,
  noneChoices: 5785,
  featuresTested: 7,
  priceLevels: 5,
  paymentOptions: 3,
};


// ─── DEFAULT ASSUMPTIONS (Funnel & Cost Model) ───
export const DEFAULT_FEATURE_COSTS: Record<string, number> = {
  'Smart Biometrics Ring': 136,   // CFO FEB 2026: fully loaded (hardware + warehousing + shipping + logistics)
  'Workout Sessions': 10,
  'Body Composition Analysis': 12,
  'Recovery Sessions': 5,
  'DNA Health Report': 75,
  'Personalized Wellness Plan': 2,
  'Nutrition Tracking': 2,
};

export const DEFAULT_ASSUMPTIONS: Assumptions = {
  s1Leads: 5300,              // Monthly S1 Leads — ProForma FEB 2026 (range: 5,300-8,501)
  kitReturnRate: 0.85,        // AlterMe Assumption Calculator — 85%
  consultRate: 0.85,          // AlterMe Assumption Calculator — 85%
  totalDeductionRate: 0.266,  // CFO FEB 2026: fees + refunds + cancels + chargebacks (~26.6%, i.e. $1,499 nets $1,100)
  s1CostPerUnit: 93.39,      // CFO FEB 2026: cost per S1 kit
  s2CloseRate: 0.20,          // CFO FEB 2026: ~20% close rate ("almost 5 S1s to get a sale")
  s2RepCost: 189,             // CFO FEB 2026: S2 rep cost per sale
  fixedCosts: 420288,         // CFO Assumption Calculator FEB 2026
  featureScaling: 0.5,        // Multiplicative dampener on total feature lift
  cannibalizationRate: 0.15,  // 15% of higher-tier demand trades down when cheaper tiers exist
  featureCosts: { ...DEFAULT_FEATURE_COSTS },
};

// Assumption metadata for tooltips and labels
export const ASSUMPTION_META: Record<keyof Assumptions, { label: string; format: string; source: string; min: number; max: number; step: number }> = {
  s1Leads:            { label: 'Monthly S1 Leads',        format: '#,##0',  source: 'ProForma FEB 2026 — range 5,300-8,501',                                              min: 1000,  max: 15000, step: 100 },
  kitReturnRate:      { label: 'Kit Return Rate',          format: '0.0%',   source: 'AlterMe Assumption Calculator — 85%',                                                min: 0.5,   max: 1.0,   step: 0.01 },
  consultRate:        { label: 'Consultation Rate',        format: '0.0%',   source: 'AlterMe Assumption Calculator — 85%',                                                min: 0.5,   max: 1.0,   step: 0.01 },
  totalDeductionRate: { label: 'Total Deduction Rate',     format: '0.0%',   source: 'CFO FEB 2026: all-in deductions (fees, refunds, cancels, chargebacks). $1,499 nets $1,100 = 26.6%', min: 0.05, max: 0.50, step: 0.005 },
  s1CostPerUnit:      { label: 'S1 Cost per Kit',          format: '$#,##0', source: 'CFO FEB 2026: cost of each S1 kit = $93.39',                                         min: 30,    max: 200,   step: 1 },
  s2CloseRate:        { label: 'S2 Close Rate',            format: '0.0%',   source: 'CFO FEB 2026: ~20% operational close rate (≈5 S1s per S2 sale)',                      min: 0.05,  max: 0.50,  step: 0.01 },
  s2RepCost:          { label: 'S2 Rep Cost / Sale',       format: '$#,##0', source: 'CFO FEB 2026: per-sale S2 rep cost = $189',                                          min: 0,     max: 500,   step: 5 },
  fixedCosts:         { label: 'Monthly Fixed Costs',      format: '$#,##0', source: 'CFO Assumption Calculator FEB 2026',                                                 min: 200000, max: 800000, step: 10000 },
  featureScaling:     { label: 'Bundle Discount Factor',   format: '0.0',    source: 'Multiplicative scaling on total feature lift (0=ignore, 1=full)',                     min: 0.0,   max: 1.0,   step: 0.1 },
  cannibalizationRate: { label: 'Cannibalization Rate',    format: '0.0%',   source: 'Estimated % of higher-tier demand that trades down when cheaper tiers exist (0=none, 50%=aggressive)', min: 0.0, max: 0.5, step: 0.05 },
  featureCosts:       { label: 'Per-Feature COGS',         format: '$#,##0', source: 'CFO FEB 2026: Ring $136 (fully loaded), DNA $75, others $31 total',                  min: 0,     max: 300,   step: 5 },
};


// ─── DEFAULT PACKAGE CONFIGURATIONS ───
import type { PackageCostOverrides } from '../types';

interface DefaultPackage {
  name: string;
  s1OfferName?: string;
  price: number;
  paymentTerm: string;
  enabledFeatures: string[];
  costOverrides?: PackageCostOverrides;
}

export const MAX_PACKAGES = 5;
export const MIN_PACKAGES = 1;

export const DEFAULT_PACKAGES: DefaultPackage[] = [
  {
    name: 'Package A',
    s1OfferName: 'DNA Kit',
    price: 1499,
    paymentTerm: 'monthlyZeroApr',
    enabledFeatures: [...FEATURE_NAMES], // all ON
  },
  {
    name: 'Package B',
    s1OfferName: 'Starter Kit',
    price: 999,
    paymentTerm: 'monthlyZeroApr',
    enabledFeatures: [FEATURE_NAMES[0], FEATURE_NAMES[1]], // Smart Ring + Workouts
    costOverrides: { s1CostPerUnit: 55, s2CloseRate: 0.25 },
  },
  {
    name: 'Package C',
    s1OfferName: 'Health Assessment',
    price: 499,
    paymentTerm: 'monthlyZeroApr',
    enabledFeatures: [FEATURE_NAMES[0]], // Smart Ring only
    costOverrides: { s1CostPerUnit: 35, s2CloseRate: 0.35 },
  },
];


// ─── FINANCIAL MODEL ASSUMPTION GAPS ───
// Current AlterMe financial model vs. what the conjoint data shows.
export const ASSUMPTION_GAPS: AssumptionGap[] = [
  { metric: 'S2 Price',              financialModel: '$1,899',                    conjointEvidence: 'Revenue per prospect peaks at $1,499',   gap: 'Consider testing lower tier', riskLevel: 'HIGH' },
  { metric: 'Close Rate',            financialModel: '30% (warm funnel)',         conjointEvidence: '11.9% cold-market demand — different metric', gap: 'Different frameworks — not a gap', riskLevel: 'LOW' },
  { metric: 'S2 Monthly Units',      financialModel: '1,719 (ProForma target)',  conjointEvidence: 'Conjoint projects ~628 at $1,899 (scenario, not forecast)', gap: 'Different frameworks', riskLevel: 'MEDIUM' },
  { metric: 'Financing Rate',        financialModel: '90%',                       conjointEvidence: '67.7% prefer monthly',             gap: 'Cash flow upside',    riskLevel: 'LOW' },
  { metric: 'Feature Priority',      financialModel: 'DNA-based',                 conjointEvidence: 'Smart Ring #1 in cold-market demand', gap: 'Marketing vs. funnel engine', riskLevel: 'MEDIUM' },
  { metric: 'Addressable Market',    financialModel: '100% implicit',             conjointEvidence: '78.3% (21.7% always-none)',        gap: '~1 in 5 unreachable', riskLevel: 'MEDIUM' },
  { metric: 'DNA Credibility',       financialModel: 'Core value prop',           conjointEvidence: 'Ranks 5th of 7 in cold-market lift', gap: 'Funnel role vs. acquisition role', riskLevel: 'MEDIUM' },
  { metric: 'Breakeven Timeline',    financialModel: 'Dec 2026 (ProForma)',      conjointEvidence: 'Conjoint has no timeline — static snapshot', gap: 'Cannot validate with conjoint', riskLevel: 'LOW' },
];


// ─── REVENUE SCENARIOS (for Financial Model tab) ───
export const REVENUE_SCENARIOS = [
  {
    name: 'Current Model (FEB GOALS)',
    price: 1899,
    choiceRate: 0.202, // their internal assumption
    s2Units: 1072,     // their target
    isCurrentModel: true,
  },
  { name: 'Conjoint Reality @ $1,899', priceIndex: 4 },
  { name: 'Conjoint @ $1,499',         priceIndex: 2 },
  { name: 'Conjoint @ $999',           priceIndex: 1 },
  { name: 'Conjoint @ $499',           priceIndex: 0 },
];


// ─── SENSITIVITY ANALYSIS S1 LEAD LEVELS ───
export const S1_LEAD_LEVELS = [3000, 4000, 5000, 5300, 6000, 7000, 8000, 8501];




// ─── METHODOLOGY NOTES (for tooltips) ───
export const METHODOLOGY_NOTES: Record<string, string> = {
  conceptLevelRate: 'Probability of choosing any given concept at a price point. Includes none-choosers in denominator. This is the most conservative demand estimate.',
  conditionalShare: 'Share among respondents who chose a product (excludes none). Higher than concept-level. Use for share-of-preference analysis.',
  featureLift: 'Difference in conditional share when feature included vs. excluded. Positive = feature adds value. Measured in isolation; bundling effects dampened by Feature Lift Scaling.',
  segmentRate: 'Concept-level rate within each k-means cluster. Captures price sensitivity heterogeneity across the three identified market segments.',
  revenuePerShowing: 'Price x concept-level choice rate. Expected revenue of showing a single concept to one respondent.',
  adjustedChoiceRate: 'Base choice rate at price * (1 + total feature lift * feature scaling). Accounts for feature bundle value above the base price-only rate.',
  impliedCloseRate: 'Adjusted choice rate / (kit return rate * consultation rate). Shows what the in-consultation close rate would need to be for the conjoint demand scenario to materialize through the actual funnel. Compare to the proven 30%.',
  featureScaling: 'Multiplicative dampener (0-1) applied to total feature lift. Accounts for diminishing returns when bundling multiple features. Default 0.5 = half of the sum-of-individual-lifts.',
  s1Leads: 'Monthly Stage 1 purchase volume. The conjoint choice rate is applied to this volume as a scenario proxy for S1-to-S2 conversion.',
  noneRate: 'Proportion of choice tasks where the respondent chose "none of these" instead of any product concept. Higher = lower overall product appeal at tested configurations.',
};
